// Motor OFFLINE del POS (script 303) — cortes de energía / sin internet:
// · Caché en IndexedDB: catálogo vendible completo, formas de pago del local,
//   clientes y bloques de secuenciales reservados por caja.
// · Cola (outbox) de ventas offline con client_uuid → la sincronización es
//   idempotente (reintentos no duplican; el server responde `duplicada`).
// · Clave de acceso SRI generada AQUÍ (49 dígitos, módulo 11 — mismo algoritmo
//   del sri-worker y de financiero._sri_clave_acceso): emisión offline (72 h).
// · Detección de conexión: navigator.onLine + fallos de red de axios + ping.
import { apiClient } from '@/lib/api/client';
import type { FormaPago, ProductoBusqueda, ClienteOpt } from './shared';

// ---------- IndexedDB mínimo (kv + outbox) ----------
const DB = 'pos-offline';
let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((res, rej) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore('kv');
        req.result.createObjectStore('outbox', { keyPath: 'client_uuid' });
      };
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
  }
  return dbp;
}
async function kvGet<T>(k: string): Promise<T | undefined> {
  const d = await db();
  return new Promise((res, rej) => {
    const r = d.transaction('kv').objectStore('kv').get(k);
    r.onsuccess = () => res(r.result as T | undefined); r.onerror = () => rej(r.error);
  });
}
async function kvSet(k: string, v: unknown): Promise<void> {
  const d = await db();
  return new Promise((res, rej) => {
    const t = d.transaction('kv', 'readwrite');
    t.objectStore('kv').put(v, k);
    t.oncomplete = () => res(); t.onerror = () => rej(t.error);
  });
}
// kv genérico para otros módulos del POS (Restaurant: snapshots del plano/cuentas)
export const cacheGet = kvGet;
export const cacheSet = kvSet;

// ---------- Clave de acceso (ficha técnica SRI, espejo del worker) ----------
function digitoModulo11(base48: string): string {
  let peso = 2, suma = 0;
  for (let i = base48.length - 1; i >= 0; i--) {
    suma += Number(base48[i]) * peso;
    peso = peso === 7 ? 2 : peso + 1;
  }
  let dv = 11 - (suma % 11);
  if (dv === 11) dv = 0;
  if (dv === 10) dv = 1;
  return String(dv);
}
export interface DatosBloque {
  ruc: string; ambiente: string; establecimiento: string; punto_emision: string; tipo_emision: string;
  bloques: { desde: number; hasta: number; usado: number }[];   // usado = último secuencial consumido (0 = ninguno)
}
export function generarClaveAcceso(d: Omit<DatosBloque, 'bloques'>, secuencial: number, fecha: Date): string {
  const dd = String(fecha.getDate()).padStart(2, '0');
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const sec = String(secuencial).padStart(9, '0');
  const base = `${dd}${mm}${fecha.getFullYear()}` + '01' + d.ruc.padStart(13, '0') + d.ambiente
    + d.establecimiento.padStart(3, '0') + d.punto_emision.padStart(3, '0') + sec + sec.slice(-8) + d.tipo_emision;
  return base + digitoModulo11(base);
}

// ---------- Estado de conexión ----------
type Sub = (online: boolean) => void;
let _online = typeof navigator !== 'undefined' ? navigator.onLine : true;
const subs = new Set<Sub>();
let pingTimer: ReturnType<typeof setInterval> | null = null;
function setOnline(v: boolean) {
  if (v === _online) return;
  _online = v;
  subs.forEach((s) => s(v));
  if (!v && !pingTimer) {
    // sin red: sondea cada 15s hasta recuperar (el ping barato NO pasa por axios)
    pingTimer = setInterval(() => { void ping(); }, 15000);
  } else if (v && pingTimer) {
    clearInterval(pingTimer); pingTimer = null;
  }
}
async function ping(): Promise<void> {
  try {
    const base: string = (apiClient.defaults.baseURL as string) || '';
    const r = await fetch(base + '/', { method: 'HEAD', cache: 'no-store', signal: AbortSignal.timeout(4000) });
    if (r.ok || r.status < 500) setOnline(true);
  } catch { /* sigue caído */ }
}
export const conexion = {
  get online() { return _online; },
  suscribir(s: Sub): () => void { subs.add(s); return () => subs.delete(s); },
};
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void ping(); });
  window.addEventListener('offline', () => setOnline(false));
  // fallo de red de CUALQUIER llamada del ERP = probable corte (el ping lo confirma/levanta)
  apiClient.interceptors.response.use(
    (r) => { setOnline(true); return r; },
    (e) => { if (e?.code === 'ERR_NETWORK' || e?.message === 'Network Error') setOnline(false); return Promise.reject(e); },
  );
}

// ---------- Snapshot del catálogo / formas / clientes ----------
export async function snapshotCatalogo(locationId: string, partnerId: string | null): Promise<number> {
  const rows = (await apiClient.post<ProductoBusqueda[]>('/rpc/pos_catalogo_offline',
    { p_location: locationId, p_partner: partnerId }, { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } })).data || [];
  await kvSet(`catalogo:${locationId}`, { rows, ts: Date.now() });
  return rows.length;
}
export async function catalogoOffline(locationId: string): Promise<ProductoBusqueda[]> {
  const c = await kvGet<{ rows: ProductoBusqueda[] }>(`catalogo:${locationId}`);
  return c?.rows ?? [];
}
export async function snapshotFormas(locationId: string | null, formas: FormaPago[]): Promise<void> {
  await kvSet(`formas:${locationId ?? 'all'}`, formas);
}
export async function formasOffline(locationId: string | null): Promise<FormaPago[]> {
  return (await kvGet<FormaPago[]>(`formas:${locationId ?? 'all'}`)) ?? [];
}
export async function snapshotClientes(clientes: ClienteOpt[]): Promise<void> {
  await kvSet('clientes', clientes);
}
export async function clientesOffline(): Promise<ClienteOpt[]> {
  return (await kvGet<ClienteOpt[]>('clientes')) ?? [];
}

// Búsqueda local (F1 y escáner) sobre el catálogo cacheado
export function buscarEnCatalogo(rows: ProductoBusqueda[], q: string, categoria?: string | null, marca?: string | null, pa?: string | null): ProductoBusqueda[] {
  if (categoria) return rows.filter((p) => (p as unknown as { categoria_id?: string }).categoria_id === categoria).slice(0, 200);
  if (marca) return rows.filter((p) => p.marca_id === marca).slice(0, 200);
  if (pa) return rows.filter((p) => (p.principio_activo ?? '').toLowerCase() === pa.toLowerCase()).slice(0, 200);
  const t = q.trim().toLowerCase();
  if (!t) return rows.slice(0, 30);
  return rows.filter((p) => p.nombre.toLowerCase().includes(t) || p.codigo.toLowerCase().includes(t)).slice(0, 200);
}
export function porCodigoEnCatalogo(rows: ProductoBusqueda[], code: string): ProductoBusqueda | undefined {
  const c = code.trim().toLowerCase();
  return rows.find((p) => {
    const b = p as unknown as { codigo_barras1?: string | null; codigo_barras2?: string | null; codigo_barras3?: string | null };
    return p.codigo.toLowerCase() === c
      || (b.codigo_barras1 ?? '').toLowerCase() === c || (b.codigo_barras2 ?? '').toLowerCase() === c || (b.codigo_barras3 ?? '').toLowerCase() === c;
  });
}
// Descuenta stock del catálogo cacheado (optimista) tras una venta offline
export async function descontarStockLocal(locationId: string, items: { producto_id: string; cantidad: number }[]): Promise<void> {
  const c = await kvGet<{ rows: ProductoBusqueda[]; ts: number }>(`catalogo:${locationId}`);
  if (!c) return;
  for (const it of items) {
    const p = c.rows.find((r) => r.id === it.producto_id);
    if (p) p.stock = Number(p.stock) - it.cantidad;
  }
  await kvSet(`catalogo:${locationId}`, c);
}

// ---------- Bloques de secuenciales (clave de acceso offline) ----------
export async function asegurarBloque(cajaId: string, deviceId: string): Promise<void> {
  const k = `bloque:${cajaId}`;
  const d = (await kvGet<DatosBloque>(k)) ?? null;
  const restantes = d?.bloques.reduce((s, b) => s + (b.hasta - Math.max(b.usado, b.desde - 1)), 0) ?? 0;
  if (d && restantes >= 10) return;
  const r = (await apiClient.post<{ desde: number; hasta: number; ruc: string; ambiente: string; establecimiento: string; punto_emision: string; tipo_emision: string }>(
    '/rpc/pos_bloque_reservar', { _caja_id: cajaId, _device_id: deviceId, _tam: 50 },
    { headers: { 'Content-Profile': 'pos', 'Accept-Profile': 'pos' } })).data;
  const nuevo: DatosBloque = {
    ruc: r.ruc, ambiente: r.ambiente, establecimiento: r.establecimiento,
    punto_emision: r.punto_emision, tipo_emision: r.tipo_emision,
    bloques: [...(d?.bloques ?? []).filter((b) => b.usado < b.hasta), { desde: r.desde, hasta: r.hasta, usado: r.desde - 1 }],
  };
  await kvSet(k, nuevo);
}
// Consume el siguiente secuencial reservado y devuelve la clave/serie (o null si no hay bloque)
export async function claveOffline(cajaId: string): Promise<{ secuencial: number; clave: string; serie: string } | null> {
  const k = `bloque:${cajaId}`;
  const d = await kvGet<DatosBloque>(k);
  if (!d) return null;
  const b = d.bloques.find((x) => x.usado < x.hasta);
  if (!b) return null;
  const secuencial = Math.max(b.usado, b.desde - 1) + 1;
  b.usado = secuencial;
  await kvSet(k, d);
  const clave = generarClaveAcceso(d, secuencial, new Date());
  const serie = `${d.establecimiento}-${d.punto_emision}-${String(secuencial).padStart(9, '0')}`;
  return { secuencial, clave, serie };
}
export async function bloqueDisponible(cajaId: string): Promise<number> {
  const d = await kvGet<DatosBloque>(`bloque:${cajaId}`);
  return d?.bloques.reduce((s, b) => s + (b.hasta - Math.max(b.usado, b.desde - 1)), 0) ?? 0;
}

// ---------- Outbox de ventas offline ----------
export interface VentaOffline {
  client_uuid: string; fecha: string; caja_id: string; caja_nombre: string;
  total: number; tipo_doc: string; serie: string | null; error?: string | null;
  payload: Record<string, unknown>;
  // Restaurant (323): el replay va por rest_cuenta_cobrar en vez del motor directo
  rpc?: 'pos_registrar_venta' | 'rest_cuenta_cobrar';
  args?: Record<string, unknown>;
}
export async function encolarVenta(v: VentaOffline): Promise<void> {
  const d = await db();
  return new Promise((res, rej) => {
    const t = d.transaction('outbox', 'readwrite');
    t.objectStore('outbox').put(v);
    t.oncomplete = () => res(); t.onerror = () => rej(t.error);
  });
}
export async function ventasPendientes(): Promise<VentaOffline[]> {
  const d = await db();
  return new Promise((res, rej) => {
    const r = d.transaction('outbox').objectStore('outbox').getAll();
    r.onsuccess = () => res((r.result as VentaOffline[]).sort((a, b) => a.fecha.localeCompare(b.fecha)));
    r.onerror = () => rej(r.error);
  });
}
async function quitarDeCola(clientUuid: string): Promise<void> {
  const d = await db();
  return new Promise((res, rej) => {
    const t = d.transaction('outbox', 'readwrite');
    t.objectStore('outbox').delete(clientUuid);
    t.oncomplete = () => res(); t.onerror = () => rej(t.error);
  });
}
export async function descartarVentaOffline(clientUuid: string): Promise<void> { return quitarDeCola(clientUuid); }

// Drena la cola EN ORDEN. Devuelve {ok, fallidas}. Errores de negocio (respuesta del
// server) marcan la venta y siguen; errores de red abortan (seguimos offline).
let sincronizando = false;
export async function sincronizarVentas(): Promise<{ ok: number; fallidas: number }> {
  if (sincronizando) return { ok: 0, fallidas: 0 };
  sincronizando = true;
  let ok = 0, fallidas = 0;
  try {
    const cola = await ventasPendientes();
    for (const v of cola) {
      try {
        if (v.rpc === 'rest_cuenta_cobrar') {
          // cobro de cuenta Restaurant: el server cierra la cuenta y descarga
          // recetas; con client_uuid repetido responde {duplicada} (idempotente)
          await apiClient.post('/rpc/rest_cuenta_cobrar', v.args ?? {},
            { headers: { 'Content-Profile': 'pos', 'Accept-Profile': 'pos' } });
        } else {
          await apiClient.post('/rpc/pos_registrar_venta', { payload: v.payload },
            { headers: { 'Content-Profile': 'pos', 'Accept-Profile': 'pos' } });
        }
        await quitarDeCola(v.client_uuid);
        ok++;
      } catch (e) {
        const err = e as { code?: string; message?: string; response?: { data?: { message?: string } } };
        if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') { setOnline(false); break; }
        v.error = err?.response?.data?.message ?? err?.message ?? 'Error desconocido';
        await encolarVenta(v);   // conserva con el error visible para revisión manual
        fallidas++;
      }
    }
  } finally { sincronizando = false; }
  return { ok, fallidas };
}
